import type { ReactNode } from 'react';

import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import type { QuestionSummary } from 'models/api';
import type { Product } from 'models/product';
import type { ProductCard } from 'models/productCard';
import { filterTruthy } from 'utils/collection';

import { useStockContext } from '../ProductDetailsContext';
import ProductFit from '../ProductFit';

import BasePicker from './BasePicker';

interface Props {
	className?: string;
	fallbackButtonText: string;
	fallbackPopoverTitle: string;
	fit3Summary: QuestionSummary | undefined;
	fitText: string | undefined;
	isLoadingFit: boolean;
	product: Product;
	sizeGuidePopoverButton: ReactNode;
	variants: ProductCard[] | undefined;
}

export default function SizePicker({
	className,
	fallbackButtonText,
	fallbackPopoverTitle,
	fit3Summary,
	fitText,
	isLoadingFit,
	product,
	sizeGuidePopoverButton,
	variants,
}: Props) {
	const { isLoadingVariantsStock, loadVariantsStock, variantsStock } =
		useStockContext();

	return (
		<>
			<BasePicker
				className={className}
				buttonLabel={
					product.multipleVariantNaming?.pickerText || fallbackButtonText
				}
				buttonSupportingAction={sizeGuidePopoverButton}
				buttonSupportingText={fitText}
				popoverTitle={
					product.multipleVariantNaming?.pickerText || fallbackPopoverTitle
				}
				onOpen={() => {
					loadVariantsStock();
				}}
				shouldScrollToTopOnSelect={false}
				selectedText={product.variantText}
				stockData={variantsStock}
				stockIsLoading={isLoadingVariantsStock}
				popoverStartContent={
					<ProductFit
						infoTextClassName="mb-6"
						fit3Summary={fit3Summary}
						showScale={false}
					/>
				}
				variants={filterTruthy(variants, 'url').map((variant) => ({
					isSelected: variant.id === product.id,
					isSellable: variant.isSellable,
					isSellableOnWeb: variant.isSellableOnWeb,
					key: variant.id,
					price: variant.displayPrice,
					title: variant.variantText,
					url: variant.url,
					variantId: variant.id,
				}))}
			/>
			{isLoadingFit && (
				<LoadingSkeleton>
					<LoadingSkeletonItem height="text-sm" className="mt-1 w-44" />
				</LoadingSkeleton>
			)}
		</>
	);
}
SizePicker.displayName = 'ProductDetails_SizePicker';
